import { Box, Button, ButtonLink, Flex, Heading, Notice, Tag, Text } from '@applyboard/crystal-ui'
import { ChevronLeftOutlineIcon } from '@applyboard/ui-icons'
import { StudentApplication } from '../../../components/Application/types'
import { NavBars } from '../../../components/NavBars'
import { ApplicationSteps } from '../../../utils/enums'
import { CreateApplicationErrorBoundary } from '../CreateApplicationErrorBoundary'
import { ProgramFilters } from '../ProgramFilters'
import { ProgramList } from '../ProgramList'

type SelectAlternateProgramProps = Readonly<{
  application: StudentApplication | null
  isUpdatingApplication: boolean
  setCurrentStep: (step: ApplicationSteps) => void
  handleUpdateApplication: (
    primaryIntakeId: string | null,
    alternateIntakeId: string[] | null,
  ) => void
  currentApplicationId: string
  onBackToProgramSelection: () => void
}>

export function SelectAlternateProgram(props: SelectAlternateProgramProps) {
  const alternateProgramSelected = (
    props.application?.attributes?.alternateProgramSelected ?? []
  ).find(alternateProgram => alternateProgram.programIntakeId)
  const isEditing = props.application?.attributes?.alternateProgramSelected !== undefined

  return (
    <NavBars maxWidth={680}>
      <CreateApplicationErrorBoundary>
        <Text>
          {props.application?.attributes?.alternateProgramSelected ? (
            <ButtonLink
              href={`/applications/${props.currentApplicationId}`}
              leadIcon={ChevronLeftOutlineIcon}
              variant="transparent"
            >
              Back to Application details
            </ButtonLink>
          ) : (
            <Button
              leadIcon={ChevronLeftOutlineIcon}
              variant="transparent"
              onClick={props.onBackToProgramSelection}
            >
              Back to Program selection
            </Button>
          )}
        </Text>
        <Flex gap={6} direction="column">
          <Flex
            align={{ xs: 'start', md: 'center' }}
            direction={{ xs: 'column', md: 'row' }}
            gap={4}
          >
            <Heading level={1} variant="headlineM">
              {isEditing ? 'Edit Alternate Program' : 'Alternate Program'}
            </Heading>
            <Tag intent="secondary" size="md">
              Optional
            </Tag>
          </Flex>

          <Notice intent="secondary">
            <Flex direction={{ xs: 'column', md: 'row' }} gap={4}>
              <Text variant={{ xs: 'bodyL', md: 'bodyM' }}>
                Adding an alternate program may increase your chances of admission.
              </Text>
              <Flex justify="center" pr={{ xs: 10, md: 0 }}>
                <Notice.Button
                  onClick={() => {
                    props.handleUpdateApplication(null, [])
                  }}
                  disabled={props.isUpdatingApplication}
                  loading={props.isUpdatingApplication}
                >
                  <Text variant={{ xs: 'titleS', md: 'labelL' }} contrast="high">
                    {isEditing && props.application?.attributes?.alternateProgramSelected?.length
                      ? 'Remove alternate program'
                      : 'Skip this step'}
                  </Text>
                </Notice.Button>
              </Flex>
            </Flex>
          </Notice>

          <ProgramFilters />
          <ProgramList
            loading={props.isUpdatingApplication}
            onSubmit={alternateIntakeId => {
              props.handleUpdateApplication(null, [alternateIntakeId])
            }}
            currentApplication={props.application?.attributes}
            primarySelectedProgramId={
              props.application?.attributes?.programSelected?.programIntakeId
            }
            alternateProgramSelected={alternateProgramSelected?.programIntakeId}
            buttonEmphasis="highlighted"
          />
        </Flex>
      </CreateApplicationErrorBoundary>
    </NavBars>
  )
}
